import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionActions from '@mui/material/AccordionActions'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { DisciplineIcons } from '@components/DisciplineIcons'
import type { Projectbetrokkene } from '@models/Projectbetrokkene'

import { disciplineLabels } from '../../core/util/discipline'
import { PropertyRow } from '../../theme/components/PropertyRow'
import { StyledIconButton } from '../../theme/components/StyledIconButton'

export { ProjectbetrokkenenCard }

type Props = {
  projectbetrokkenen: Projectbetrokkene[]
  expanded?: boolean
  showCreateBetrokkene?: boolean
  onClickCreateBetrokkene: () => void
  showDeleteBetrokkene?: boolean
  onClickDeleteBetrokkene: (projectbetrokkene: Projectbetrokkene) => void
  showEditDisciplines?: boolean
  onClickEditDisciplines: (projectbetrokkene: Projectbetrokkene) => void
}

const ProjectbetrokkenenCard = ({
  projectbetrokkenen,
  expanded = false,
  showCreateBetrokkene = false,
  onClickCreateBetrokkene,
  showDeleteBetrokkene = false,
  onClickDeleteBetrokkene,
  showEditDisciplines = false,
  onClickEditDisciplines,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(expanded)

  return (
    <Accordion
      onChange={(_, expanded) => setIsExpanded(expanded)}
      expanded={isExpanded}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Fade in={!isExpanded} appear={false} unmountOnExit>
          <Typography>
            {projectbetrokkenen[0].verantwoordelijke +
              ' - ' +
              projectbetrokkenen[0].gebiedscodeLabel}
          </Typography>
        </Fade>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12 }}>
            <PropertyRow
              label="Gebied"
              value={projectbetrokkenen[0].gebiedscodeLabel}
              variant="h5"
            />
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <PropertyRow
              label="Verantwoordelijke"
              value={projectbetrokkenen[0].verantwoordelijke}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 8 }}>
            <PropertyRow
              label="Gebiedscode"
              value={projectbetrokkenen[0].gebiedscode}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organisatie</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Delegerende</TableCell>
                  <TableCell>Disciplines</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {projectbetrokkenen.map((projectbetrokkene) => {
                  const { organisatie, type, delegerende, disciplines } =
                    projectbetrokkene

                  return (
                    <TableRow key={organisatie + type + delegerende} hover>
                      <TableCell>{organisatie}</TableCell>
                      <TableCell>{type}</TableCell>
                      <TableCell>{delegerende}</TableCell>

                      <TableCell>
                        <DisciplineIcons
                          disciplineVerzamelingen={(disciplines ?? []).map(
                            (discipline) => ({
                              disciplineCode: discipline,
                              disciplineLabel: disciplineLabels[discipline],
                              netbeheerderCode: organisatie,
                              netbeheerderLabel: disciplineLabels[discipline],
                            }),
                          )}
                        />
                      </TableCell>

                      <TableCell align="right" padding="none">
                        {showEditDisciplines && type === 'Netbeheerder' && (
                          <StyledIconButton
                            title={`${organisatie} disciplines wijzigen`}
                            onClick={() =>
                              onClickEditDisciplines(projectbetrokkene)
                            }
                            icon={<EditOutlinedIcon />}
                          />
                        )}
                        {showDeleteBetrokkene && (
                          <StyledIconButton
                            title={`${organisatie} verwijderen`}
                            onClick={() =>
                              onClickDeleteBetrokkene(projectbetrokkene)
                            }
                            icon={<DeleteOutlineIcon />}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </AccordionDetails>

      {showCreateBetrokkene && (
        <AccordionActions>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={onClickCreateBetrokkene}
          >
            Betrokkene toevoegen
          </Button>
        </AccordionActions>
      )}
    </Accordion>
  )
}
